import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import QuickListEdit from "./QuickListEdit";
import ConfirmDialog from "./ConfirmDialog";
import { dateTimeToTime, Capitalize } from "components/Shared/Formatters";
import { getQuickListShareGuid, copyQuickList } from "requests/quickList";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import WebConfig from "middlewares/WebConfig";
import QuickFilterStatusIcon from "components/QuickLists/QuickFilterStatusIcon.tsx";

class QuickList extends Component {
	static contextType = DialogContext;
  constructor(props) {
    super(props);
    this.onApplyClick = this.onApplyClick.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onShareClick = this.onShareClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.onEditorClick = this.onEditorClick.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.onCopyClick = this.onCopyClick.bind(this);
    this.onDeleteConfirmed = this.onDeleteConfirmed.bind(this);
    this.onMenuClick = this.onMenuClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);

    this.getResult = this.getResult.bind(this);
    this.renderEditor = this.renderEditor.bind(this);
    this.isMobile = this.isMobile.bind(this);
    this.mounted = { current: true };
    this.state = {
      showEditor: false,
      showDeletor: false,
      menuAnchorEl: null,
    };
  }

  componentDidMount() { this.onRefresh(); }
  componentWillUnmount() { this.mounted.current = false; }
  isMobile() { return window.innerWidth < 1200; }

  onDeleteClick(e) { e.stopPropagation(); this.setState({ showDeletor: true }); }
  onDeleteConfirmed() { this.setState({ showDeletor: false }); this.props.onDelete(this.props.item); }
  onEditorClick(e) { e.stopPropagation(); this.setState({ showEditor: true }); }
  onCloseClick() { this.setState({ showEditor: false, showDeletor: false }); }
  onRefresh() { this.props.getCount(this.props.item); }
  getResult() { this.props.getResult(this.props.item); }
  onApplyClick(quickList) { this.setState({ showEditor: false }); this.props.onSave(quickList); }
  onMenuClick(e) { e.stopPropagation(); this.setState({ menuAnchorEl: e.currentTarget }); }
  onMenuClose() { this.setState({ menuAnchorEl: null }); }

  onClick() {
    if (!this.state.showEditor) {
      if (this.props.onClick) {
        this.props.onClick(this.props.item.id);
      }
      else {
        this.onRefresh();
        this.getResult();
      }
    }
  }

  onShareClick(e) {
    e.stopPropagation();
    const { orthosRoot } = WebConfig;

    this.context.showOk("Share Quick List", "Loading quicklist share url...");

    getQuickListShareGuid(this.props.item.id, shareGuid => {
      let url = `${orthosRoot}/webui/get-shared-quick-list?shareGuid=${shareGuid}`;
      const copyUrl = () => navigator.clipboard.writeText(url);
      this.context.showYesNo("Share Quick List", "Press Copy Url to copy the quick list share url", copyUrl, null, "Copy Url", "Cancel");
    }, this.mounted);
  }

  onCopyClick() {
    copyQuickList(this.props.item.id, this.props.onReloadLists, this.mounted);
  }

  renderEditor() {
    const { currentGroupNames } = this.props;

    return (ReactDOM.createPortal(
      <QuickListEdit
        templateList={this.props.templateList}
        {...{ currentGroupNames }}
        id={this.props.item.id}
        item={this.props.item}
        onApply={this.onApplyClick}
        onClose={this.onCloseClick} />,
      document.getElementById("quicklist-container"),
    ));
  }

  render() {
    const { item, isSelected, canEdit, isSharedWithThisUser } = this.props;
    let countDisplay = "...";
    let updateDisplay = "...";
    if (item.count && item.count.asOfDate) {
      updateDisplay = "Updated: " + dateTimeToTime(item.count.asOfDate);
      countDisplay = item.count.recordCount;
    }
    let menuItems = [];
    if (canEdit) {
      menuItems.push(
        <IconButton key={1} onClick={this.onEditorClick} size="small" color="secondary">
          <Icon>edit</Icon>
        </IconButton>
      );
      if (this.props.canShare) menuItems.push(
        <IconButton key={2} onClick={this.onShareClick} size="small" color="secondary">
          <Icon>share</Icon>
        </IconButton>
      );
    }

    if (canEdit || isSharedWithThisUser) {
      menuItems.push(
        <IconButton key={3} onClick={this.onCopyClick} size="small" color="secondary">
          <Icon>content_copy</Icon>
        </IconButton>
      );
      if (item.id > 0) menuItems.push(
        <IconButton key={4} onClick={this.onDeleteClick} size="small" color="secondary">
          <Icon>delete</Icon>
        </IconButton>
      );
      menuItems.push(
        <IconButton key={5} onClick={this.onClick} size="small" color="secondary">
          <Icon>refresh</Icon>
        </IconButton>
      );
    }

    return (
      <>
        <Card className="quicklist text-overflow-hidden" style={{ cursor: "pointer" }}>
          <div className={`md-card-title ${isSelected ? "selected" : ""}`} onClick={this.onClick}>
            <div className="md-card-title--title">{Capitalize(item.name)}</div>
          </div>
          <div className="md-card-text" style={{ position: "relative" }} onClick={this.onClick}>
            <div className="md-text-center md-display-2 nomargin quick-list-count font-black">
              <QuickFilterStatusIcon baseQuery={item.templateCode} />
              {countDisplay}
            </div>
            <div className="nomargin md-caption quicklist-footer">
              {updateDisplay}
              {menuItems.length ?
                <>
                  <IconButton
                    id="quicklist-menu"
                    className="quicklist-menu-button"
                    onClick={this.onMenuClick}
                    size="small"
                  >
                    <Icon>more_vert</Icon>
                  </IconButton>
                  <Menu
                    anchorEl={this.state.menuAnchorEl}
                    open={Boolean(this.state.menuAnchorEl)}
                    onClose={() => this.onMenuClose()}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    sx={{
                      "& .MuiPaper-root": {
                        display: "flex",
                        flexDirection: "row",
                      }
                    }}
                  >
                    {menuItems}
                  </Menu>
                </> : null
              }
              {isSelected && !menuItems.length && <Icon>lock</Icon>}
            </div>
          </div>
        </Card>
        {this.state.showEditor && this.renderEditor()}
        {this.state.showDeletor && <ConfirmDialog
          title="Confirm Quick List Delete"
          message={item.name}
          onOkClick={this.onDeleteConfirmed}
          onCancelClick={this.onCloseClick} />}
      </>
    );
  }
}
QuickList.propTypes = {
  templateList: PropTypes.array.isRequired,
  currentGroupNames: PropTypes.array,
  item: PropTypes.object.isRequired,
  getCount: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canShare: PropTypes.bool.isRequired,
  isSharedWithThisUser: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  getResult: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReloadLists: PropTypes.func.isRequired,
};

export default QuickList;